/**
 * TenantDetail — complete + operate a single tenant (Phase 9C + 13).
 *
 * Route: /tenants/:tenantId   (super_admin only)
 *
 * A super_admin creates a tenant PENDING; it cannot go live until its brokerage
 * profile is filled (Phase 13 activation gate). This screen is where that happens:
 * the brokerage-details form + the Activate button + custom-domain management.
 *
 * Reads:  GET  /admin/tenants                 (find this tenant + its status)
 *         GET  /admin/brokerage-settings?tenant_id=…
 *         GET  /admin/tenants/{id}/domains
 * Writes: PATCH /admin/brokerage-settings?tenant_id=…
 *         POST  /admin/tenants/{id}/activate
 *         POST/DELETE/verify …/domains
 */

import { useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adminService } from "@re-platform/api-client";
import type {
  TenantDomain,
  TenantStatus,
  TenantWithPlan,
} from "@re-platform/api-client";
import { api } from "../../lib/api";
import { cardList, cardShell } from "../../lib/cards";
import { BrokerageProfile } from "./MyBrokerage";

const PAGES_CNAME_TARGET = "re-platform-web-90u.pages.dev";

const inputStyle: React.CSSProperties = {
  padding: "6px 8px",
  border: "1px solid var(--rp-color-border)",
  borderRadius: "var(--rp-radius-sm)",
  fontSize: 13,
  width: 260,
};

function VerifiedBadge({ verified_at }: { verified_at: string | null }) {
  if (verified_at) {
    return (
      <span
        style={{
          fontSize: 11,
          padding: "2px 8px",
          borderRadius: 99,
          background: "var(--rp-color-success-faint)",
          color: "var(--rp-color-success)",
        }}
      >
        verified
      </span>
    );
  }
  return (
    <span
      style={{
        fontSize: 11,
        padding: "2px 8px",
        borderRadius: 99,
        background: "var(--rp-color-warning-faint)",
        color: "var(--rp-color-warning)",
      }}
    >
      pending DNS
    </span>
  );
}

function DomainRow({
  tenantId,
  domain,
  onToast,
}: {
  tenantId: string;
  domain: TenantDomain;
  onToast: (msg: string) => void;
}) {
  const queryClient = useQueryClient();

  const verifyMut = useMutation({
    mutationFn: () => adminService.verifyTenantDomain(api, tenantId, domain.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["tenant-domains", tenantId],
      });
      onToast(`${domain.domain} verified.`);
    },
    onError: (err) =>
      onToast(err instanceof Error ? err.message : "Verify failed"),
  });

  const deleteMut = useMutation({
    mutationFn: () => adminService.deleteTenantDomain(api, tenantId, domain.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["tenant-domains", tenantId],
      });
      onToast(`${domain.domain} removed.`);
    },
    onError: (err) =>
      onToast(err instanceof Error ? err.message : "Delete failed"),
  });

  return (
    <div
      className="rp-clientcard"
      style={{ ...cardShell, alignItems: "start" }}
    >
      <div style={{ flex: 1, minWidth: 220 }}>
        <div
          style={{ fontFamily: "monospace", fontSize: 13.5, fontWeight: 600 }}
        >
          {domain.domain}
          {domain.is_primary && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 400,
                color: "var(--rp-color-text-muted)",
              }}
            >
              (primary)
            </span>
          )}
        </div>
        <div
          style={{
            fontFamily: "monospace",
            fontSize: 12,
            color: "var(--rp-color-text-muted)",
            marginTop: 2,
          }}
        >
          rp_id {domain.rp_id}
        </div>
        {/* The CNAME an unverified domain still needs. It used to be a second
            table row spanning every column; on a card it belongs to the domain
            it describes. */}
        {!domain.verified_at && !domain.is_primary && (
          <div style={{ marginTop: 8 }}>
            <p
              style={{
                margin: "0 0 4px",
                fontSize: 12,
                color: "var(--rp-color-text-muted)",
              }}
            >
              Add this CNAME to your DNS provider, then click Check DNS:
            </p>
            <code
              style={{
                fontSize: 12,
                background: "var(--rp-color-surface-2)",
                padding: "3px 8px",
                borderRadius: 4,
                display: "inline-block",
                wordBreak: "break-all",
              }}
            >
              {domain.domain} → {PAGES_CNAME_TARGET}
            </code>
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginLeft: "auto",
        }}
      >
        <VerifiedBadge verified_at={domain.verified_at} />
        {!domain.verified_at && (
          <button
            type="button"
            className="rp-btn rp-btn--secondary rp-btn--sm"
            disabled={verifyMut.isPending}
            onClick={() => verifyMut.mutate()}
          >
            {verifyMut.isPending ? "…" : "Check DNS"}
          </button>
        )}
        {!domain.is_primary && (
          <button
            type="button"
            className="rp-btn rp-btn--danger rp-btn--sm"
            disabled={deleteMut.isPending}
            onClick={() => {
              if (confirm(`Remove ${domain.domain}?`)) deleteMut.mutate();
            }}
          >
            {deleteMut.isPending ? "…" : "Remove"}
          </button>
        )}
      </div>
    </div>
  );
}

function AddDomainForm({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState("");
  const [rpId, setRpId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      adminService.addTenantDomain(api, tenantId, {
        domain: domain.trim().toLowerCase(),
        rp_id: rpId.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["tenant-domains", tenantId],
      });
      setDomain("");
      setRpId("");
      setError(null);
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : "Add failed"),
  });

  const canSubmit = domain.trim().length >= 3 && !mutation.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
      style={{
        ...cardShell,
        padding: 16,
        marginBottom: 24,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        gap: 16,
      }}
    >
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 13,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>Domain</span>
        <input
          value={domain}
          placeholder="app.myman.com"
          onChange={(e) => setDomain(e.target.value)}
          style={inputStyle}
        />
        <span style={{ fontSize: 11, color: "var(--rp-color-text-muted)" }}>
          fully-qualified hostname the brokerage will use
        </span>
      </label>
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          fontSize: 13,
        }}
      >
        <span style={{ color: "var(--rp-color-text-muted)" }}>
          RP ID <span style={{ fontWeight: 400 }}>(optional)</span>
        </span>
        <input
          value={rpId}
          placeholder="myman.com"
          onChange={(e) => setRpId(e.target.value)}
          style={{ ...inputStyle, width: 180 }}
        />
        <span style={{ fontSize: 11, color: "var(--rp-color-text-muted)" }}>
          auto-computed from last 2 labels
        </span>
      </label>
      <button
        type="submit"
        className="rp-btn rp-btn--primary rp-btn--sm"
        disabled={!canSubmit}
      >
        {mutation.isPending ? "Adding…" : "Add domain"}
      </button>
      {error && (
        <span style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
          {error}
        </span>
      )}
    </form>
  );
}

const STATUS_STYLE: Partial<Record<TenantStatus, { bg: string; fg: string }>> =
  {
    ACTIVE: {
      bg: "var(--rp-color-success-faint)",
      fg: "var(--rp-color-success)",
    },
    PENDING: {
      bg: "var(--rp-color-warning-faint)",
      fg: "var(--rp-color-warning)",
    },
    SUSPENDED: {
      bg: "var(--rp-color-danger-faint)",
      fg: "var(--rp-color-danger)",
    },
  };

const STATUS_FALLBACK = {
  bg: "var(--rp-color-surface-raised)",
  fg: "var(--rp-color-text-muted)",
};

function StatusBadge({ status }: { status: TenantStatus }) {
  const s = STATUS_STYLE[status] ?? STATUS_FALLBACK;
  return (
    <span
      style={{
        fontSize: 11,
        padding: "2px 10px",
        borderRadius: 99,
        background: s.bg,
        color: s.fg,
        fontWeight: 600,
      }}
    >
      {status}
    </span>
  );
}

/**
 * Everything you can do TO a tenant, in one reusable block.
 *
 * Mounted twice: inline under a card on the Tenants roster (the Manage expand,
 * matching how Staff manages an agent), and standalone on /tenants/:tenantId so
 * a deep link still works. Extracted rather than duplicated — the two would
 * have drifted, and the domain half is the only place a custom domain can be
 * registered or DNS re-checked at all.
 *
 * Sub-sections collapse independently, the same shape as a Staff Manage panel
 * and My brokerage: this is a lot of form, and it should open to a summary
 * rather than a wall.
 */
export function TenantManagePanel({
  tenantId,
  status,
  onToast,
}: {
  tenantId: string;
  status: TenantStatus | undefined;
  onToast: (msg: string) => void;
}) {
  const domains = useQuery<TenantDomain[]>({
    queryKey: ["tenant-domains", tenantId],
    queryFn: () => adminService.listTenantDomains(api, tenantId),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });

  return (
    <>
      {status === "PENDING" && (
        <p
          style={{
            fontSize: 13,
            color: "var(--rp-color-text-muted)",
            margin: "0 0 16px",
          }}
        >
          This brokerage is not live yet. Fill in its identity, address and
          licence details, then Activate — activation is refused until the
          profile is complete, and it names exactly what is still missing.
        </p>
      )}

      {/* The very same profile a broker edits on My brokerage, pointed at this
          tenant. The domain controls take the slot where a broker is told the
          operator manages their domain — because here, that IS the operator. */}
      <BrokerageProfile
        tenantId={tenantId}
        statusExtra={
          <div style={{ marginTop: 12 }}>
            <AddDomainForm tenantId={tenantId} />
            {domains.isLoading && (
              <p style={{ color: "var(--rp-color-text-muted)", fontSize: 13 }}>
                Loading…
              </p>
            )}
            {domains.isError && (
              <p style={{ color: "var(--rp-color-danger)", fontSize: 13 }}>
                {String(domains.error)}
              </p>
            )}
            {domains.data && domains.data.length > 0 && (
              <div style={cardList}>
                {domains.data.map((d) => (
                  <DomainRow
                    key={d.id}
                    tenantId={tenantId}
                    domain={d}
                    onToast={onToast}
                  />
                ))}
              </div>
            )}
          </div>
        }
      />
    </>
  );
}

export function TenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [toast, setToast] = useState<string | null>(null);
  const queryClient = useQueryClient();

  function onToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  // No single get-tenant endpoint; the list is the source of the current status.
  const tenantQ = useQuery({
    queryKey: ["tenants-list-for-detail"],
    queryFn: () => adminService.listTenants(api),
    enabled: Boolean(tenantId),
    select: (rows: TenantWithPlan[]) => rows.find((t) => t.id === tenantId),
    staleTime: 15_000,
  });
  const tenant = tenantQ.data;

  const activate = useMutation({
    mutationFn: () => adminService.activateTenant(api, tenantId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["tenants-list-for-detail"],
      });
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      onToast(
        "Tenant activated — its domain now resolves and clients can sign up.",
      );
    },
    // The 422 gate lists exactly what's still missing; surface it verbatim.
    onError: (err) =>
      onToast(err instanceof Error ? err.message : "Activate failed"),
  });

  return (
    <>
      <div
        style={{
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Link
          to="/tenants"
          style={{ fontSize: 13, color: "var(--rp-color-text-muted)" }}
        >
          ← Tenants
        </Link>
        <h1 style={{ margin: 0 }}>{tenant?.display_name ?? "Tenant"}</h1>
        {tenant && <StatusBadge status={tenant.status} />}
        {tenant?.status === "PENDING" && (
          <button
            type="button"
            className="rp-btn rp-btn--primary rp-btn--sm"
            style={{ marginLeft: "auto" }}
            disabled={activate.isPending}
            onClick={() => activate.mutate()}
          >
            {activate.isPending ? "Activating…" : "Activate tenant"}
          </button>
        )}
      </div>

      {toast && (
        <div
          style={{
            background: "var(--rp-color-surface-raised)",
            border: "1px solid var(--rp-color-border)",
            borderRadius: "var(--rp-radius-sm)",
            padding: "8px 12px",
            marginBottom: 16,
            fontSize: 13,
            whiteSpace: "pre-wrap",
          }}
        >
          {toast}
        </div>
      )}

      {tenantId && (
        <TenantManagePanel
          tenantId={tenantId}
          status={tenant?.status}
          onToast={onToast}
        />
      )}
    </>
  );
}
